<template>
    <div id="root">
        <!-- Seção de erro 404 -->
        <section id="notFound" v-if="!validScenario">
            <h1>{{ errorCode }}</h1>
            <h2>{{ $t('unavailable_scenario') }}</h2>
            <p>{{ errorMessage }}</p>
        </section>

        <div v-if="loading" class="loading-container">
            <i class="fas fa-spinner fa-spin"></i>
            <p>Carregando...</p>
        </div>

        <main v-else>
              <!--Header -->
              <header class="header">
                  <nav class="header-container">
                      <button v-if="showPrevWeek" @click="changeWeek(-1)" class="nav-button">
                          <i class="fas fa-angle-left"></i>
                      </button>
  
                      <div class="central-container">
                          <button class="date-button" @click="toggleCalendar">
                              <i class="far fa-calendar"></i>
                              <p>{{ formattedWeek }}</p>
                          </button>
                      </div>
  
                      <button v-if="showNextWeek" @click="changeWeek(1)" class="nav-button">
                          <i class="fas fa-angle-right"></i>
                      </button>
                  </nav>
              </header>

              <!-- Overlay para pegar interações fora do calendário -->
              <div v-if="showCalendar" 
                   class="overlay" 
                   @click="toggleCalendar" 
                   @touchstart="toggleCalendar" 
                   @mousedown="toggleCalendar" 
                   @pointerdown="toggleCalendar"
              />
  
              <!-- Calendário (Date Picker) -->
              <aside v-if="showCalendar" class="calendar-dropdown">
                  <header class="calendar-header">
                      <span>{{ currentMonthYear }}</span>
                      <button @click="toggleCalendar" class="close-button">
                          <i class="fas fa-times"></i>
                      </button>
                  </header>
                  <section class="calendar-grid">
                      <div v-for="day in weekDays" :key="day" class="calendar-day">
                          {{ day }}
                      </div>
                      <button v-for="date in calendarDays" 
                              :key="date.toString()" 
                              @click="selectDate(date)" 
                              :class="dateClass(date)" 
                              class="dateDay">
                          {{ date.date() }}
                      </button>
                  </section>
              </aside>
  
              <!-- Container de categorias (apenas mobile) -->
              <section class="category-container">
                  <button v-for="(category, index) in categoriesList" 
                          :key="category.seq" 
                          @click="selectCategory(category.seq)" 
                          :class="['category-button', { 'active': String(selectedCategory) === String(category.seq) }]">
                      {{ category.nomeFantasia || category.name }}
                  </button>
              </section>

              <!-- Visualização Mobile -->
              <section class="mobile-view">
                  <section class="content-container">
                      <article v-for="date in weekDates" 
                               :key="date.toString()" 
                               :class="['card', { 'card-disabled': !dishForDay(date).nomePrato }]">
                          <div class="card-content">
                              <div class="card-date">
                                  <div class="date-number">{{ formatDate(date, 'D') }}</div>
                                  <div class="date-day">{{ formatCustomDate(date) }}</div>
                              </div>
              
                              <div class="card-info">
                                  <h3 class="dish-name">{{ dishForDay(date).nomeFantasiaPrato || dishForDay(date).nomePrato || noDishString }}</h3>
                                  <div class="dish-details">
                                      <i class="fas fa-users"></i>
                                      {{ dishForDay(date).nomePrato ? getExpectedDiners(date, dishForDay(date).percentualConsumo) : 0 }} {{ $t('people') }}
                                  </div>
                              </div>
                          </div>
                      </article>
                  </section>
              </section>
              
              <!-- Visualização Desktop -->
              <section class="desktop-view">
                  <article v-for="(category, index) in categoriesList" :key="category.seq" class="desktop-category">
                      <h2 class="category-title">{{ category.nomeFantasia || category.name }}</h2>
              
                      <div class="cards-column">
                          <article v-for="date in weekDates" 
                                   :key="category.seq + '-' + date.toString()" 
                                   :class="['card', { 'card-disabled': !dishForDayAndCategory(date, index).nomePrato }]">
                              <div class="card-content">
                                  <div class="card-date">
                                      <div class="date-number">{{ formatDate(date, 'D') }}</div>
                                      <div class="date-day">{{ formatCustomDate(date) }}</div>
                                  </div>
              
                                  <div class="card-info">
                                      <h3 class="dish-name">{{ dishForDayAndCategory(date, index).nomeFantasiaPrato || dishForDayAndCategory(date, index).nomePrato || noDishString }}</h3>
                                      <div class="dish-details">
                                          <i class="fas fa-users"></i>
                                          {{ dishForDayAndCategory(date, index).nomePrato ? getExpectedDiners(date, dishForDayAndCategory(date, index).percentualConsumo) : 0 }} {{ $t('people') }}
                                      </div>
                                  </div>
                              </div>
                          </article>
                      </div>
                  </article>
              </section>


              <footer class="footer" v-if="validScenario">
                <!-- Menu de idioma flutuante -->
                <div class="language-menu">
                  <!-- Ícone da língua atual -->
                  <img
                    class="current-lang"
                    :src="currentIcon"
                    alt="Idioma atual"
                    @click="toggleMenu"
                  />
                  <!-- Ícones das outras línguas (são exibidos somente se showMenu for true) -->
                  <div class="other-lang" v-if="showMenu">
                    <!-- Exibe somente se a língua atual não for BR -->
                    <img
                      v-if="appLanguage !== 'pt-BR'"
                      :src="BrIcon"
                      alt="Português"
                      @click="changeLang('pt-BR')"
                    />
                    <!-- Exibe somente se a língua atual não for USA -->
                    <img
                      v-if="appLanguage !== 'en-US'"
                      :src="UsaIcon"
                      alt="English"
                      @click="changeLang('en-US')"
                    />
                    <!-- Exibe somente se a língua atual não for ES -->
                    <img
                      v-if="appLanguage !== 'es-CL'"
                      :src="EsIcon"
                      alt="Español"
                      @click="changeLang('es-CL')"
                    />
                  </div>
                </div>
                
                <button @click="toggleRankingOverlay" class="ranking-button">
                  <span>{{ $t('see_ranking') }}</span>
                  <i class="fas fa-external-link-alt"></i>
                </button>
              </footer>
        </main>

        <!-- Ranking  -->
        <section class="ranking-overlay" v-if="rankingVisible">

          <header class="ranking-header">
            <button @click="toggleRankingOverlay" class="back-button">
              <i class="fas fa-arrow-left"></i>
            </button>
            <h2>{{ $t('month_ranking') }}</h2>
          </header>

          <section class="ranking-cards">
              <article v-for="(category, index) in ranking" :key="category.seq" class="card ranking-card">
                  <div class="card-content">
                      <div class="card-info">
                          <div class="ranking">
                              <h3 class="dish-name">{{ category.gppratoNomeFantasia || category.gppratoNome }}</h3>
                              <div class="progress-bar">
                                  <div class="progress-fill" :style="{ width: category.incidencia + '%' }"></div>
                              </div>
                          </div>
                          <div class="dish-details ranking-dish-details">
                              <i class="fas fa-utensils"></i>
                              {{ category.incidencia }} {{ $t('times') }}
                          </div>
                      </div>
                  </div>
              </article>
          </section>

        </section>
    </div>
</template>

<script>
/*global localStorage*/
import moment from "moment";
import requestModule from "@/application/libs/requestModule";
import CryptoJS from 'crypto-js';
import { t } from 'i18next';
// import ProdENV from '@/../config/prod.env.js';

export default {
  name: "Cenario",
    data() {
        return {
            view: {
              title: 'Cenário Compartilhado'
            },
            cenario: null,
            validScenario: false,
            errorMessage: '',
            errorCode: 404,
            currentMonth: moment(),
            selectedDate: moment(),
            selectedCategory: 'main',
            showCalendar: false,
            rankingVisible: false,
            loading: true,
            appLanguage: localStorage.getItem("lang") || "pt-BR",
            BrIcon: require("@/../static/brasil.png"),
            UsaIcon: require("@/../static/estados-unidos.png"),
            EsIcon: require("@/../static/espanha.png"),
            showMenu: false,
            noDishString: t('no_dish')
        };
    },

    async created() {
        const codigo = this.$route.params.codigo;
        const [NRORG, NRCENARIOCARD, DTEXPIRACAO] = this.decodeEncryptedBase64(codigo).split('_');
        if (this.isValidDate(DTEXPIRACAO)) {
            this.validScenario = true;
            
            requestModule.postRequest('PublicScenario', {
                PARAMS: {
                    NRCENARIOCARD,
                    NRORG,
                    DTEXPIRACAO
                }
            }).then(response => {
              this.loading = false;

              if(response.error && response.errorCode){
                this.errorMessage = response.error;
                this.errorCode = response.errorCode;
                this.validScenario = false;
                return;
              }
              
              this.cenario = response.dataset.data[0];

              // define a categoria padrão (primeiro sequencial do primeiro dia)
              if (this.cenario.dias?.length > 0 && this.cenario.dias[0].pratos.length > 0) {
                  this.selectedCategory = this.cenario.dias[0].pratos[0].sequencial;
              }
              // define o primeiro dia do cenário
              const firstDayStr = this.cenario.dias[0].data;
              const scenarioStart = moment(firstDayStr, "DD/MM/YYYY");
              this.selectedDate = scenarioStart.clone();
              this.currentMonth = scenarioStart.clone();
            });

            return;
        }

        const formattedDate = moment(DTEXPIRACAO, "DD/MM/YYYY").format("D [de] MMMM [de] YYYY");
        this.errorMessage = `${t('expired_link_date_message')} ${formattedDate}`;
    },
    
    mounted() {
      document.title = `${this.view.title} - Cardápio Inteligente`;
    },

    computed: {
        scenarioStart() {
            if (!this.cenario?.dias?.length) return moment();
            return moment(this.cenario.dias[0].data, "DD/MM/YYYY");
        },

        scenarioEnd() {
            if (!this.cenario?.dias?.length) return moment();
            return moment(this.cenario.dias[this.cenario.dias.length - 1].data, "DD/MM/YYYY");
        },

        // gera os dias para o calendário (date picker) apenas dentro do cenário
        calendarDays() {
            let days = [];
            let day = this.scenarioStart.clone();
            while (day.isSameOrBefore(this.scenarioEnd, 'day')) {
                days.push(day.clone());
                day.add(1, 'day');
            }
            return days;
        },

        // gera os dias da semana para a visualização (mobile)
        weekDates() {
            const startWeek = this.selectedDate.clone().startOf('week');
            let dates = [];
            for (let i = 0; i < 7; i++) {
                let day = startWeek.clone().add(i, 'days');
                if (day.isSameOrAfter(this.scenarioStart, 'day') && day.isSameOrBefore(this.scenarioEnd, 'day')) {
                    dates.push(day);
                }
            }
            return dates;
        },

        formattedWeek() {
            const dates = this.weekDates;
            if (!dates.length) return '';
            const start = dates[0].format('DD/MM');
            const end = dates[dates.length - 1].format('DD/MM');
            return `${start} - ${end}`;
        },

        showPrevWeek() {
            const weekStart = this.selectedDate.clone().startOf('week');
            return weekStart.isAfter(this.scenarioStart, 'day');
        },

        showNextWeek() {
            const weekEnd = this.selectedDate.clone().endOf('week');
            return weekEnd.isBefore(this.scenarioEnd, 'day');
        },

        weekDays() {
            let days = [];
            for (let i = 0; i < 7; i++) {
              days.push(moment().locale(this.appLanguage).day(i).format('ddd'));
            }
            return days;
        },

        currentMonthYear() {
            return this.currentMonth.locale(this.appLanguage).format('MMMM YYYY');
        },

        categoriesList() {
            if (!this.cenario?.dias?.length) return [];
            const seen = new Set();
            return (this.cenario.dias[0].pratos || []).reduce((acc, prato) => {
                if (!seen.has(prato.sequencial)) {
                    seen.add(prato.sequencial);
                    acc.push({
                        seq: prato.sequencial,
                        name: prato.nmTipoPrato || prato.sequencial,
                        nomeFantasia: prato.nmFantasiaTipoPrato || null,
                    });
                }
                return acc;
            }, []);
        },

        ranking() {
          return this.cenario.ranking;
        },

        selectedCategoryIndex() {
            const cats = this.categoriesList;
            return cats.findIndex(cat => String(cat.seq) === String(this.selectedCategory));
        },
        
        currentIcon() {
          switch (this.appLanguage) {
            case 'pt-BR':
              return this.BrIcon;
            case 'en-US':
              return this.UsaIcon;
            case 'es-CL':
              return this.EsIcon;
            default:
              return this.BrIcon;
          }
        }
    },

    methods: {
        getCategorySecondaryName(categoryIndex) {
          const referenceDate = this.weekDates[0];
          const dish = this.dishForDayAndCategory(referenceDate, categoryIndex);
          return dish && dish.gppratoNome ? dish.gppratoNome : '';
        },

        getExpectedDiners(date, percentualConsumo) {
            if (!this.cenario?.dias) return '-';
            const dateStr = date.format("DD/MM/YYYY");
            const diaObj = this.cenario.dias.find(d => d.data === dateStr);
            if (!diaObj) return '-';
            return Math.round(diaObj.qtde_comensais * percentualConsumo / 100);
        },

        dishForDay(day) {
            if (!this.cenario?.dias) return {};
            const dateStr = day.format("DD/MM/YYYY");
            const diaObj = this.cenario.dias.find(d => d.data === dateStr);
            if (!diaObj) return {};
            const cat = this.categoriesList[this.selectedCategoryIndex];
            if (!cat) return {};
            return diaObj.pratos.find(p => p.sequencial === cat.seq) || {};
        },

        dishForDayAndCategory(day, categoryIndex) {
            if (!this.cenario?.dias) return {};
            const dateStr = day.format("DD/MM/YYYY");
            const diaObj = this.cenario.dias.find(d => d.data === dateStr);
            if (!diaObj) return {};
            const cat = this.categoriesList[categoryIndex];
            if (!cat) return {};
            return diaObj.pratos.find(p => p.sequencial === cat.seq) || {};
        },

        decodeEncryptedBase64(base64Text) {
          // reverte o base64
          const normalBase64 = base64Text
            .replace(/-/g, '+')
            .replace(/_/g, '/');

          // descriptografa o AES
          const decrypted = CryptoJS.AES.decrypt(normalBase64, process.env.AES_KEY);
          return decrypted.toString(CryptoJS.enc.Utf8);
        },

        openDatePicker() {
            this.$refs.dateInput.showPicker();
        },

        toggleCalendar() {
            this.showCalendar = !this.showCalendar;
        },

        selectDate(date) {
            this.selectedDate = moment(date);
            this.showCalendar = false;
        },

        changeWeek(offset) {
            this.selectedDate = this.selectedDate.clone().add(offset, 'week');
        },

        dateClass(date) {
            return {
                'today': date.isSame(moment(), 'day'),
                'selected': date.isSame(this.selectedDate, 'day'),
                'other-month': !date.isSame(this.currentMonth, 'month'),
            };
        },

        formatDate(date, format) {
            return moment(date).format(format);
        },

        formatCustomDate(date) {
            const dayOfWeek = moment(date).locale(this.appLanguage).format('ddd');

            if (dayOfWeek === 'sábado' || dayOfWeek === 'domingo') {
                return dayOfWeek.charAt(0) + dayOfWeek.slice(1);
            }

            const firstWord = dayOfWeek.split('-')[0];
            return firstWord.charAt(0) + firstWord.slice(1);
        },

        selectCategory(categoryId) {
            this.selectedCategory = categoryId;
        },

        updateSelectedDate() {
            this.selectedDate = moment(this.selectedDateFormatted, 'YYYY-MM-DD');
        },

        isValidDate(dateStr) {
            const format = "DD/MM/YYYY";
            const date = moment(dateStr, format, true);
            const today = moment().startOf('day');
            return date.isValid() && date.isSameOrAfter(today);
        },
        
        toggleRankingOverlay() {
          this.rankingVisible = !this.rankingVisible;
        },
        
        getProgress(category) {
          return Math.floor(Math.random() * 100) + 1;
        },
        
        // Alterna a exibição do menu de línguas
        toggleMenu() {
          this.showMenu = !this.showMenu;
        },
        
        // Altera a língua e fecha o menu
        changeLang(lang) {
          this.appLanguage = lang;
          this.showMenu = false;
          localStorage.setItem("lang", lang);
        }
    },

};
</script>

<style scoped lang="less">
* {
  margin: 0;
  padding: 0;
  box-sizing: border-box;
}

#root {
  background: #f9fafb;
  height: 100%;
  overflow-x: hidden;
}

*::-webkit-scrollbar {
  display: none;
}

#notFound {
  /*background: red;*/
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;

  h1 {
    font-size: 5rem;
    color: #e11d48;
  }

  h2 {
    font-size: 2rem;
  }

  p {
    font-size: 1.3rem;
  }
}

.loading-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100vh; // ajuste conforme necessário
  font-size: 1.2rem;
  color: #555;
}

main{
  /*position: relative;*/
  /*height: 100vh;*/
  /*background: red;*/
}

/*Header style*/
.header {
  position: sticky;
  top: 0;
  z-index: 50;
  background: white;
  /*box-shadow: 0px 2px 4px rgba(0, 0, 0, 0.1);*/
  padding: 1rem 1rem 0;
}

.header-container {
  max-width: 1200px;
  margin: auto;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.central-container {
  display: flex;
  flex-grow: 1;
  justify-content: center;
}

.nav-button {
  font-size: 1.25rem;
  border-radius: 50%;
  padding: 0.5rem 0.8rem;

  background: transparent;
  cursor: pointer;
  border: none;
}

.hidden-date-input {
  position: absolute;
  top: 30px;
  margin-right: 15%;
  opacity: 0;
  pointer-events: none;
}

.date-button {
  display: flex;
  gap: 10px;
  align-items: end;

  padding: 0.5rem 1rem;
  border-radius: 8px;
  font-size: 1.25rem;

  background: white;
  cursor: pointer;
  border: none;

  p {
    font-size: 1rem;
    font-weight: 600;
  }
}

.overlay {
  position: fixed;
  top: 70px;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 998;
}

.calendar-dropdown {
  position: absolute;
  top: 70px;
  left: 50%;
  width: 100%;
  transform: translateX(-50%);
  background: white;
  /*border: 1px solid #ddd;*/
  padding: 10px;
  box-shadow: 0px 2px 4px rgba(0, 0, 0, 0.1);
  z-index: 999;

  border-radius: 0 0 0.6rem 0.6rem;
  /*background: red;*/
}

.calendar-header {
  display: flex;
  justify-content: space-between;
  padding: 5px;
  font-weight: bold;

  .close-button {
    font-size: 1.25rem;
  }
}

@media screen and (min-width: 600px) {
  .calendar-header {
    span {
      margin-left: 3%;
    }
  }
}

@media screen and (min-width: 900px) {
  .calendar-header {
    span {
      margin-left: 5%;
    }
  }
}

.calendar-grid {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  text-align: center;
}

button {
  background: none;
  border: none;
  cursor: pointer;
  padding: 5px;

  border-radius: 8px;
}

.dateDay {
  height: 2.25rem;
  min-width: fit-content;
}

@media screen and (min-width: 769px) {
  .today,
  .selected {
    width: 50%;
    margin: auto;
  }
}

.today {
  background: #ffdddd;
}

.selected {
  background: #ff5555;
  color: white;
}

.other-month {
  color: #ccc;
}

.date-button:hover,
.nav-button:hover {
  background: #f0f0f0;
}

/*Category Style*/
.category-container {
  position: sticky;
  top: 0;
  z-index: 60;

  display: flex;
  gap: 0.5rem;
  padding: 1rem;
  border-radius: 0 0 0.6rem 0.6rem;

  box-shadow: rgba(0, 0, 0, 0.1) 0px 4px 6px -1px,
    rgba(0, 0, 0, 0.06) 0px 2px 4px -1px;
  background: white;
  overflow-x: scroll;
}

.category-button {
  min-width: fit-content;
  padding: 0.5rem 1rem;

  border-radius: 16px;
  background: #e5e5e5;
  cursor: pointer;

  border: none;
}
.category-button.active {
  background: #e11d48;
  color: white;
}

/*Content Style*/
.content-container {
  padding: 1rem;
}

.card {
  background: white;
  border-radius: 8px;
  box-shadow: 0px 2px 4px rgba(0, 0, 0, 0.1);
  padding: 1rem;
  transition: 0.3s;

  margin-bottom: 16px;
}

.card-disabled {
  background-color: #f0f0f0;
  color: #a0a0a0;
  cursor: not-allowed;
  opacity: 0.6;
}

.card:hover {
  box-shadow: 0px 4px 8px rgba(0, 0, 0, 0.2);
}

.card-content {
  display: flex;
  gap: 12px;
}

.card-date {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;

  text-align: center;
  font-size: 1.2rem;
  font-weight: bold;
  min-width: 20%;
}

.date-number {
  font-size: 1.5rem;
  line-height: 2rem;
  font-weight: 700;

  color: rgb(17 24 39);
}

.date-day {
  font-size: 0.875rem;
  text-transform: uppercase;
  font-weight: 500;

  color: rgb(107 114 128);
}

.card-info {
  width: 100%;
  height: fit-content;

  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 9px;


}

.ranking{
  display: flex;
  gap: 10px;
  align-items: center;
  
  h3 {
    width: 60%;
    margin: 0;
    padding: 0;
  }
}

.ranking-cards {
  display: grid;
  grid-template-columns: repeat(1, 1fr);
  gap: 1rem;
  padding: 1rem;
  margin: 0 auto;
  max-width: 1200px;
  width: 100%;
}

.ranking-card {
  background: white;
  border-radius: 8px;
  box-shadow: 0px 2px 4px rgba(0, 0, 0, 0.1);
  padding: 1rem;
  transition: 0.3s;
}

.ranking-card:hover {
  box-shadow: 0px 4px 8px rgba(0, 0, 0, 0.2);
}

.dish-name {
  font-size: inherit;
  font-weight: 500;
  color: rgb(17, 24, 39);
  
  display: -webkit-box;
  -webkit-line-clamp: 1;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.dish-name:hover{
  display: inline;
  /*-webkit-line-clamp: 1;*/
  -webkit-box-orient: vertical;
  /*overflow: hidden;*/
}


.dish-details {
  font-size: 0.875rem;
  line-height: 1.25rem;
  color: rgb(107 114 128);
}

.ranking-dish-details{
  text-align: right;
}

/* Mobile view: exibido até 767px */
.mobile-view {
  display: block;
}

/* Desktop view: oculto por padrão */
.desktop-view {
  display: none;
}

/* Media queries para alternar as views */
@media screen and (min-width: 768px) {
  .mobile-view {
    display: none;
  }

  .desktop-view {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 1.5rem;

    padding: 0 3% 3%;
  }

  .header {
    padding-bottom: 1rem;
    margin-bottom: 1rem;
    box-shadow: rgba(0, 0, 0, 0.1) 0px 4px 6px -1px,
      rgba(0, 0, 0, 0.06) 0px 2px 4px -1px;
    border-radius: 0 0 0.6rem 0.6rem;
  }

  .calendar-dropdown,
  .overlay {
    top: 55px;
  }

  .category-container {
    display: none;
  }

  .card {
    margin-bottom: 0;
  }
}

@media screen and (min-width: 1024px) {
  .desktop-view {
    grid-template-columns: repeat(3, 1fr);
  }
  
  .ranking-cards {
    max-width: 100%;
    grid-template-columns: repeat(2, 1fr); /* 3 colunas em telas maiores */
  }
}

@media screen and (min-width: 1280px) {
  .desktop-view {
    grid-template-columns: repeat(5, 1fr);
  }
  
  .ranking-cards {
    grid-template-columns: repeat(3, 1fr); /* 5 colunas em telas muito grandes */
  }
}

.desktop-category {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.category-title {
  font-size: 1.125rem;
  font-weight: 600;
  color: #1f2937;
  padding: 0.5rem 0.5rem;
}

.cards-column {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

/* Botão de ranking */
.footer {
  position: fixed;
  bottom: 1rem;
  right: 1rem;
  left: 1rem;
  display: flex;
  justify-content: space-between;
  z-index: 100;
}

/* Container do menu de idiomas */
.language-menu {
  position: relative;
  display: inline-block;
}

/* Ícone da língua atual */
.current-lang {
  width: 40px;
  height: 40px;
  cursor: pointer;
  border-radius: 50%;
  padding: 1px;
}

/* Container dos ícones das outras línguas */
.other-lang {
  position: absolute;
  top: -100px;
  left: 0; /* Pode ajustar conforme sua necessidade */
  background: transparent;

  display: flex;
  flex-direction: column;
}

/* Estilo para os ícones das outras línguas */
.other-lang img {
  width: 40px;
  height: 40px;
  cursor: pointer;
  margin: 5px 0;
  border-radius: 50%;
  transition: transform 0.2s;
  
  /*padding: 10px;*/
}

.other-lang img:hover {
  transform: scale(1.1);
}

.ranking-button {
  background-color: #ef4444;
  color: #ffffff;
  border: none;
  border-radius: 9999px;
  padding: 0.75rem 1.5rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  box-shadow: 0 10px 15px -3px rgba(239, 68, 68, 0.5),
              0 4px 6px -2px rgba(239, 68, 68, 0.4);
  cursor: pointer;
  transition: background-color 0.3s;
}

.ranking-button:hover {
  background-color: #dc2626;
}

.ranking-button i {
  width: 1.25rem;
  height: 1.25rem;
}

/* Ranking Overlay */
.ranking-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: #ffffff;
  z-index: 1100;
  overflow-y: auto;
  /*padding: 1rem;*/
}


.ranking-header {
  position: sticky;
  top: 0;
  left: 0;
  right: 0;
  z-index: 50;
  
  display: flex;
  gap: 1rem;
  padding: 1rem;
  background: white;
  
  border-radius: 0 0 0.6rem 0.6rem;
  box-shadow: rgba(0, 0, 0, 0.1) 0px 4px 6px -1px,
    rgba(0, 0, 0, 0.06) 0px 2px 4px -1px;
}

.back-button {
  background: none;
  border: none;
  font-size: 1.2rem;
  cursor: pointer;
}

.ranking-header h2 {
  font-size: 1.2rem;
  margin: 0;
  font-weight: 600;
}


/* Progress Bar */
.progress-bar {
  width: 100%;
  height: 1rem;
  background: #e5e5e5;
  border-radius: 0.5rem;
  overflow: hidden;
  margin-top: 0.5rem;
}

.progress-fill {
  height: 100%;
  background: #ef4444;
  width: 0;
  transition: width 0.3s;
}

</style>
